import { processFormThumbnail } from "@utils/image-upload";
import { ICONS } from "@assets/icons";
import Button from "@components/atoms/Button";
import ContentBox from "@components/atoms/ContentBox";
import CopyButton from "@components/atoms/CopyButton";
import IconBox from "@components/atoms/IconBox";
import RenderEventCategoryContent from "@components/molecules/RenderEventCategoryContent";
import SizeNoticeBox from "@components/molecules/SizeNoticeBox";
import { PrivateRoutes } from "@constants/urls";
import { useV2EventsControllerFetchEventDetail, useV2EventsControllerUpdateEvent, useV2EventsControllerDeleteEvent, type V2EventUpdateRequestDto, V2EventUpdateRequestDtoCategory } from "@everon-it/api-client/contents-gw";
import useAuthStore from "@store/auth";
import useGlobalModal from "@hooks/useGlobalModal";
import useGlobalNoti from "@hooks/useGlobalNoti";
import useRouter from "@hooks/useRouter";
import { colors, typo } from "@styles/index";
import { RenderCommonElementByType } from "@utils/DynamicComponentUtils";
import isCheckValueExistence from "@utils/check-value-existence";
import { compareObjects } from "@utils/data-fns";
import isPreviewValiDate from "@utils/preview-validate";
import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { EventDetailData, type EventDetailInitValuesType } from "./Data";

const EventDetail: React.FC = () => {
    // Props
    const HEADER_TITLE = "이벤트 등록";
    const CONTENT_TITLE = "이벤트 내용";
    const PREVIEW_URL = "event";

    // Data
    const { createBodyKey, formInitialValues, contentsOption } = EventDetailData;

    // Hooks
    const { query, goBack, pathname } = useRouter();
    const { hideModal, showConfirmModal } = useGlobalModal();
    const { showEditNoti, showDeleteNoti } = useGlobalNoti();
    const { userInfo } = useAuthStore();
    const uuid = String(query.id || "");
    const [initData, setInitData] = useState<EventDetailInitValuesType>();

    // api-client: 상세 조회
    const { data: detailData } = useV2EventsControllerFetchEventDetail(uuid, {
        query: {
            enabled: !!uuid,
            retry: false,
            refetchOnWindowFocus: false,
        },
    });

    // api-client: 수정 mutation
    const { mutate: editMutate } = useV2EventsControllerUpdateEvent({
        mutation: {
            onSuccess: () => {
                showEditNoti({});
                goBack();
            },
        },
    });

    // api-client: 삭제 mutation
    const { mutate: deleteMutate } = useV2EventsControllerDeleteEvent({
        mutation: {
            onSuccess: () => {
                showDeleteNoti({});
                goBack();
            },
        },
    });

    // Formik
    const formik = useFormik({
        initialValues: formInitialValues,
        onSubmit: async values => {
            // 이미지 업로드 처리
            const uploadedThumbnailUrl = await processFormThumbnail(values.thumbnailUrl, "event");

            // contentCategory에 따라 body 구성
            const baseBody = {
                title: values.title,
                isVisible: values.isVisible,
                eventStartAt: values.eventStartAt,
                eventEndAt: values.eventEndAt,
                displayStartAt: values.displayStartAt,
                displayEndAt: values.displayEndAt,
                thumbnailUrl: uploadedThumbnailUrl,
                updatedBy: userInfo?.name ?? "",
            };

            let body: V2EventUpdateRequestDto;

            if (values.contentCategory === V2EventUpdateRequestDtoCategory.CONTENT) {
                body = {
                    ...baseBody,
                    category: V2EventUpdateRequestDtoCategory.CONTENT,
                    content: values.content,
                };
            } else if (values.contentCategory === V2EventUpdateRequestDtoCategory.URL) {
                body = {
                    ...baseBody,
                    category: V2EventUpdateRequestDtoCategory.URL,
                    urls: [{ url: values.url, title: "" }],
                };
            } else {
                // button
                body = {
                    ...baseBody,
                    category: V2EventUpdateRequestDtoCategory.BUTTON,
                    urls: (values.urls as { title: string; url: string }[] | undefined)?.map((item, idx) => ({
                        ...item,
                        displayOrder: idx,
                    })),
                    content: values.content,
                };
            }

            editMutate({ uuid, data: body });
        },
    });

    // 상세 데이터 formik에 반영
    useEffect(() => {
        if (detailData?.row) {
            const row = detailData.row;
            const customData: EventDetailInitValuesType = {
                title: row.title,
                isVisible: row.isVisible,
                displayStartAt: row.displayStartAt ?? "",
                displayEndAt: row.displayEndAt ?? "",
                eventStartAt: row.eventStartAt ?? "",
                eventEndAt: row.eventEndAt ?? "",
                thumbnailUrl: row.thumbnailUrl ?? "",
                contentCategory: row.category,
                content: row.content ?? "",
                url: row.category === V2EventUpdateRequestDtoCategory.URL && row.urls?.[0] ? ((row.urls[0] as { url?: string }).url ?? "") : "",
                urls: row.category === V2EventUpdateRequestDtoCategory.BUTTON ? (row.urls as { title: string; url: string }[]) : undefined,
            };
            setInitData(customData);
            void formik.setValues(customData);
        }
    }, [detailData]);

    // Functions
    const handleGoBackClick = () => {
        if (JSON.stringify(initData) !== JSON.stringify(formik.values)) {
            showConfirmModal({
                title: `작성한 내용을 저장하지 않고\n 취소 하시겠어요?`,
                firstButtonText: "닫기",
                secondButtonText: "확인",
                onConfirm: () => {
                    hideModal();
                    goBack();
                },
            });
        } else {
            goBack();
        }
    };

    const handlePreview = () => {
        const previewData = formik.values;
        localStorage.setItem("previewData", JSON.stringify(previewData));
        window.open(`${PrivateRoutes.PreView}?type=${PREVIEW_URL}`, "_blank");
    };

    const handleEditContent = () => {
        showConfirmModal({
            title: `저장하시겠어요?`,
            firstButtonText: "닫기",
            secondButtonText: "저장하기",
            onConfirm: () => {
                localStorage.removeItem("previewData");
                hideModal();
                formik.handleSubmit();
            },
        });
    };

    const handleDeleteContent = () => {
        showConfirmModal({
            title: `삭제하시겠어요?`,
            firstButtonText: "닫기",
            secondButtonText: "삭제하기",
            onConfirm: () => {
                deleteMutate({ uuid });
                hideModal();
            },
        });
    };

    const isEditButtonValidate = () => {
        if (!initData) {
            return true;
        }
        const { values: formikValues } = formik;

        return (
            !isCheckValueExistence({
                formikValues,
                pathname,
                validateKey: createBodyKey,
            }) || compareObjects(initData, formikValues)
        );
    };

    const isPreviewValidateCheck = useCallback(() => {
        return !isPreviewValiDate({
            formikValues: formik.values,
            pathname,
        });
    }, [formik.values]);

    useEffect(() => {
        // initData가 설정된 후에만 카테고리 변경 감지하여 초기화 진행
        if (initData && initData.contentCategory !== formik.values.contentCategory) {
            void formik.setFieldValue("content", "");
            void formik.setFieldValue("url", "");
            void formik.setFieldValue("urls", undefined);
        }
    }, [formik.values.contentCategory, initData]);

    return (
        <ContentsWrap>
            <HeaderWrap>
                <IconBox width={32} height={32} src={ICONS.ARROW_ICON.BACK} isCursorPointer onClick={handleGoBackClick} />
                <HeaderTitle>{HEADER_TITLE}</HeaderTitle>
            </HeaderWrap>
            <div>
                <ContentBox style={{ padding: 0 }}>
                    <ContentsHeader>
                        {CONTENT_TITLE}
                        <ButtonWrap>
                            <CopyButton text="외부 링크 복사" type="external" />
                            <CopyButton text="앱 연결 링크 복사" type="app" />
                        </ButtonWrap>
                    </ContentsHeader>
                    <ContentsMainWrap>
                        {contentsOption?.map(({ label, name, options, isSizeQuestion, size, notice, ...res }, _) => {
                            return (
                                <React.Fragment key={`${_}_contents`}>
                                    <InputWrap>
                                        <LabelWrap>
                                            <LabelInnerWrap>
                                                <Label>{label || "LABEL"}</Label>
                                                {isSizeQuestion && <SizeNoticeBox />}
                                            </LabelInnerWrap>
                                        </LabelWrap>
                                        <InputContentsWrap noti={!!notice}>
                                            {RenderCommonElementByType({
                                                formik,
                                                name,
                                                options,
                                                size,
                                                ...res,
                                            })}
                                            {notice}
                                        </InputContentsWrap>
                                    </InputWrap>
                                </React.Fragment>
                            );
                        })}
                        <RenderEventCategoryContent formik={formik} category={formik.values.contentCategory} />
                    </ContentsMainWrap>
                </ContentBox>
                <FooterButtonWrap>
                    <LeftButtonWrap>
                        <Button text="삭제" buttonType="line_red" onClick={handleDeleteContent} />
                        <Button text="미리보기" buttonType="line_primary" disabled={isPreviewValidateCheck()} onClick={handlePreview} />
                    </LeftButtonWrap>
                    <RightButtonWrap>
                        <Button text="취소" buttonType="line_black" onClick={handleGoBackClick} />
                        <Button text="저장하기" disabled={isEditButtonValidate()} onClick={handleEditContent} />
                    </RightButtonWrap>
                </FooterButtonWrap>
            </div>
        </ContentsWrap>
    );
};

export default EventDetail;

const ContentsWrap = styled.div`
    background-color: ${colors.GRAY11};
    height: 100%;
    padding: 40px;
    overflow: auto;
`;

const HeaderWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
`;

const MainHeaderTitle = styled.h1`
    ${typo.DISPLAY_3}
    color: ${colors.GRAY2};
    margin-bottom: 24px;
`;

const HeaderTitle = styled(MainHeaderTitle)`
    margin: 0;
`;

const TableTitle = styled.div`
    color: ${colors.GRAY2};
    ${typo.HEADING_4};
    margin-bottom: 18px;
`;

const ContentTitle = styled(TableTitle)`
    margin: 0;
    padding: 24px;
    border-bottom: 1px solid ${colors.GRAY9};
`;

const ContentsHeader = styled(ContentTitle)`
    display: flex;
    justify-content: space-between;
`;

const ButtonWrap = styled.div`
    display: flex;
    gap: 12px;
`;

const ContentsMainWrap = styled.div`
    display: flex;
    flex-direction: column;
    gap: 32px;
    max-width: 840px;
    margin: auto;
    padding: 52px 0 80px;
`;

const InputWrap = styled.div`
    display: flex;
    gap: 12px;
    min-height: 48px;
    width: 100%;
`;

const LabelWrap = styled.span`
    min-width: 200px;
    padding: 12px 0 12px;
`;

const LabelInnerWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
`;
const Label = styled.p`
    color: ${colors.GRAY6};
    ${typo.HEADING_7};
`;

const InputContentsWrap = styled.div<{ noti?: boolean }>`
    width: 100%;
    display: flex;
    align-items: center;

    ${({ noti }) =>
        noti &&
        css`
            flex-direction: column;
            gap: 12px;
        `}
`;

const FooterButtonWrap = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 24px;
`;

const LeftButtonWrap = styled.div`
    display: flex;
    gap: 12px;
`;

const RightButtonWrap = styled.div`
    display: flex;
    gap: 12px;
`;
